/**
 * GenericRecordsTable — the product's own table styling for entities whose
 * pages render their lists inline (conversations, users, agents, capabilities,
 * evaluators, issues, prompts, datasets, playgrounds).
 *
 * Same header and row classes as the activity and agent-analytics tables, so
 * a list in the chat reads as the same list on the page. Cells format by the
 * column's declared format; a row opens where the page would open it.
 */
import React from 'react';
import { formatTimeLocal } from 'shared/components/chat/chatCellFormat';
import {
  tableHeaderRowClass,
  tableHeaderCellClass,
  tableRowClass,
  tableDataCellClass,
} from 'features/activity/styles/dataTableStyles';

function fmtDuration(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n)) return '';
  if (n < 1000) return `${Math.round(n)} ms`;
  const s = n / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)} s`;
  const m = Math.floor(s / 60);
  const rs = Math.round(s - m * 60);
  if (m < 60) return `${m}m ${rs}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m - h * 60}m`;
}

export function formatCell(value, format) {
  if (value == null || value === '') return '';
  switch (format) {
    case 'time': return formatTimeLocal(value);
    case 'currency': { const n = Number(value); return Number.isFinite(n) ? `$${n < 1 ? n.toFixed(4).replace(/0+$/, '').replace(/\.$/, '') : n.toFixed(2)}` : String(value); }
    case 'duration': return fmtDuration(value);
    case 'number': { const n = Number(value); return Number.isFinite(n) ? n.toLocaleString() : String(value); }
    case 'percent': { const n = Number(value); return Number.isFinite(n) ? `${(n <= 1 ? n * 100 : n).toFixed(0)}%` : String(value); }
    case 'bool': return value ? 'Yes' : 'No';
    case 'list': return Array.isArray(value) ? value.join(', ') : String(value);
    case 'id': { const s = String(value); return s.length > 24 ? `${s.slice(0, 8)}…${s.slice(-4)}` : s; }
    default: { const s = typeof value === 'object' ? JSON.stringify(value) : String(value); return s.length > 80 ? `${s.slice(0, 77)}…` : s; }
  }
}

export default function GenericRecordsTable({ rows, columns, onRowClick, rowKey = (r, i) => r.id ?? r.run_id ?? r.span_id ?? r.conversation_id ?? r.distinct_id ?? r.name ?? i, emptyMessage = 'No records match.' }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="flex h-24 items-center justify-center rounded-md border border-[color:var(--border-default)] bg-surface text-[12px] text-[color:var(--text-muted)]">
        {emptyMessage}
      </div>
    );
  }
  return (
    <div className="shrink-0 overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
      <div className="overflow-x-auto">
        <table className="text-left" style={{ fontFamily: 'Inter, sans-serif', width: 'max-content', minWidth: '100%' }}>
          <thead>
            <tr className={tableHeaderRowClass}>
              {columns.map((col) => (<th key={col.key} className={tableHeaderCellClass}>{col.label}</th>))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={rowKey(row, i)} onClick={() => onRowClick?.(row)} className={onRowClick ? tableRowClass : tableRowClass.replace('cursor-pointer ', '')}>
                {columns.map((col) => (
                  <td key={col.key} className={tableDataCellClass} style={{ whiteSpace: 'nowrap' }} title={typeof row[col.key] === 'string' && row[col.key].length > 80 ? row[col.key] : undefined}>
                    {formatCell(row[col.key], col.format)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
